import { PROTOCOL_VERSION, type ClientMessage, type ServerMessage } from '@aleaces/protocol';
import { applyServerProfile, getToken, setToken } from './profile';
import { getAccessToken } from './supabase';

type Handler = (msg: ServerMessage) => void;

function serverUrl(): string {
  const env = import.meta.env['VITE_SERVER_URL'] as string | undefined;
  if (env) return env;
  const proto = location.protocol === 'https:' ? 'wss://' : 'ws://';
  return proto + location.host + '/ws';
}

class NetClient {
  private ws: WebSocket | null = null;
  private handlers = new Set<Handler>();
  playerId: string | null = null;
  private pendingName: string | null = null;
  private connecting: Promise<void> | null = null;

  get connected(): boolean {
    return !!this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  onMessage(fn: Handler): () => void {
    this.handlers.add(fn);
    return () => this.handlers.delete(fn);
  }

  /** Opens the socket (once) and logs in with the stored token. Resolves after
   * the first `profile` message so callers know the account is ready. Concurrent
   * calls (e.g. React StrictMode's double effect) share one in-flight socket. */
  connect(newName?: string): Promise<void> {
    if (newName) this.pendingName = newName;
    if (this.connected) return Promise.resolve();
    if (this.connecting) return this.connecting;
    this.connecting = new Promise<void>((resolve, reject) => {
      const ws = new WebSocket(serverUrl());
      this.ws = ws;
      let settled = false;
      const timeout = setTimeout(() => {
        if (!settled) {
          ws.close();
          reject(new Error('timeout'));
        }
      }, 8000);
      ws.onopen = () => void this.sendHello();
      ws.onerror = () => {
        clearTimeout(timeout);
        if (!settled) reject(new Error('Verbindung fehlgeschlagen'));
      };
      ws.onclose = () => {
        if (this.ws === ws) this.ws = null;
        this.connecting = null;
        this.playerId = null;
        this.emit({ type: 'error', message: 'Verbindung zum Server getrennt.' });
      };
      ws.onmessage = (e) => {
        let msg: ServerMessage;
        try {
          msg = JSON.parse(String(e.data)) as ServerMessage;
        } catch {
          return;
        }
        if (msg.type === 'welcome') this.playerId = msg.playerId;
        if (msg.type === 'profile') {
          setToken(msg.token);
          applyServerProfile(msg.profile, msg.notices, { daily: msg.questResetAt, weekly: msg.weeklyResetAt });
          if (!settled) {
            settled = true;
            clearTimeout(timeout);
            this.connecting = null;
            resolve();
          }
        }
        this.emit(msg);
      };
    });
    this.connecting.catch(() => {
      this.connecting = null;
    });
    return this.connecting;
  }

  private emit(msg: ServerMessage): void {
    for (const fn of this.handlers) fn(msg);
  }

  send(msg: ClientMessage): void {
    if (this.connected) this.ws!.send(JSON.stringify(msg));
  }

  private async sendHello(): Promise<void> {
    const token = getToken();
    const supabaseToken = await getAccessToken();
    this.send({
      type: 'hello',
      v: PROTOCOL_VERSION,
      token: token ?? null,
      name: this.pendingName ?? undefined,
      supabaseToken: supabaseToken ?? undefined,
    });
    this.pendingName = null;
  }

  disconnect(): void {
    this.ws?.close();
    this.ws = null;
  }
}

export const net = new NetClient();
